import { omit } from 'underscore';
import { parse, stringify } from 'query-string';

const AGENDA_OWNED_KEYS = [
    'filters',
    'locations',
    'sublocations',
    'dates',
    'startTime',
    'endTime',
    'speakers',
    'privateFilters',
    'count',
    'offset',
    'sessionsIds',
];

const STRIPPED_KEYS = ['count', 'offset', 'sessionsIds'];

const hasValue = value => value !== undefined && value !== null && value !== '';

const cleanValue = value => Array.isArray(value) ? value.filter(hasValue) : value;

const isNonEmpty = value => Array.isArray(value) ? value.length > 0 : hasValue(value);

// Serializes the agenda filter query object into a `search` string the server can
// read back: each filter key repeats per value (?filters=2&filters=4), which is the
// shape server/modules/AgendaService.js normalizes out of req.query. Params the
// agenda does not own (widget, preview, verified, email, disableInteractions, ...)
// are carried over from the current search so a filter change does not drop them.
// `fu` is deliberately not in that list: AgendaService.getAgendaPage trades it for
// expanded `filters=` params via a 302 before the agenda ever renders, so it never
// reaches location.search on a rendered page for this helper to preserve.
export const buildAgendaFilterSearch = (currentSearch, queryObject = {}) => {
    const preserved = omit(parse(currentSearch || ''), AGENDA_OWNED_KEYS);
    const filters = Object.keys(queryObject).reduce((acc, key) => {
        if (STRIPPED_KEYS.includes(key)) {
            return acc;
        }
        const value = cleanValue(queryObject[key]);
        if (isNonEmpty(value)) {
            acc[key] = value;
        }
        return acc;
    }, {});

    const search = stringify({ ...preserved, ...filters });
    return search ? `?${search}` : '';
};

// Builds the full `history.replace` location descriptor for an agenda filter change:
// the serialized search (see buildAgendaFilterSearch above) plus the current hash,
// so a filter toggle does not silently drop a fragment an attendee arrived with
// (e.g. an anchored share link) from the URL they go on to copy.
export const buildAgendaFilterLocationDescriptor = (location, queryObject = {}) => ({
    pathname: location.pathname,
    search: buildAgendaFilterSearch(location.search, queryObject),
    hash: location.hash,
    state: { preserveScroll: true },
});
